import { PluginInformation } from '../types/info';
import { BrandImageType } from '../types/brand';
import {
  BareImage,
  Image,
  ImageRequirement,
  ImageRequirementOption,
  ImageVariantBase,
} from '../types/image';
import { firstOf, MappedItems, notUndefined } from './functional';
import { DailyProgramImage, DailyProgramImageOptimization } from '../types/dailyProgram';
import urljoin from 'url-join';

export function populate(bareImage: BareImage, brandImageTypes?: BrandImageType[]): Image {
  return {
    ...bareImage,
    imageType:
      (brandImageTypes &&
        brandImageTypes.filter(bit => bareImage?.imageType?.toString().includes(bit._id))) ||
      [],
  };
}

export const THUMBNAIL_OPTIMIZATION = 'thumbnail';
export const ORIGINAL_OPTIMIZATION = 'original';
export const CUSTOM_OPTIMIZATION = 'custom';

export function getImageRequirementOptions(plugins: PluginInformation[]): ImageRequirementOption[] {
  return [
    {
      optimization: ORIGINAL_OPTIMIZATION,
      name: 'Original',
      maxBytes: 2 * 1024 * 1024, // 2 MB
    },
    {
      optimization: THUMBNAIL_OPTIMIZATION,
      name: 'Thumbnail',
      aspect: '1',
      width: 200,
      height: 200,
      maxBytes: 50 * 1024, // 50 KB
    },
    ...firstOf(
      plugins
        .map(p => p.config.IMAGE_REQUIREMENTS)
        .filter(notUndefined)
        .flat()
        .map(c => c as ImageRequirement),
      p => p.optimization
    ),
  ];
}

export function getMissingRequirementOptions(
  plugins: PluginInformation[],
  variants?: ImageVariantBase[]
) {
  const optimizationOptions = getImageRequirementOptions(plugins);
  return optimizationOptions.filter(
    o =>
      ![ORIGINAL_OPTIMIZATION].includes(o.optimization) ||
      !variants?.some(v => v.optimization === o.optimization)
  );
}

export function getMissingImageRequirements(
  plugins: PluginInformation[],
  variants?: ImageVariantBase[]
) {
  const optimizationOptions = getImageRequirementOptions(plugins);
  return optimizationOptions
    .filter(o => ![THUMBNAIL_OPTIMIZATION, ORIGINAL_OPTIMIZATION].includes(o.optimization))
    .filter(o => !variants?.some(v => v.optimization === o.optimization));
}

export function getDailyProgramImageOptimizations(
  images: DailyProgramImage[],
  imgDict: MappedItems<BareImage>,
  imageRequirements?: ImageRequirement[]
): DailyProgramImageOptimization[] {
  return images.map(img => ({
    fallbackUrl: img.url,
    optimizations: (imageRequirements ?? []).reduce((acc, ir) => {
      const url = imgDict[img.url]?.variants?.find(v => v.optimization === ir.optimization)?.key;
      if (url) acc[ir.optimization] = { url };
      return acc;
    }, {} as Record<string, { url: string }>),
  }));
}

export function getOptimizedImageUrl<T extends Pick<BareImage, 'key'>>(
  image: T | undefined,
  variants: ImageVariantBase[] | undefined,
  optimization: string
): string | undefined {
  const variant = variants?.find(v => v.optimization === optimization);
  return variant?.key ?? image?.key;
}

export function getOptimizedEdpImageUrl(
  images: DailyProgramImage[],
  imageOptimizations: DailyProgramImageOptimization[] | undefined,
  imageRequirement: ImageRequirement[] | undefined,
  reqIdx: number,
  imgIdx?: number
): string | undefined {
  let fallback = images[imgIdx ?? 0]?.url;
  if (!imageOptimizations) return fallback;

  const img = imageOptimizations[imgIdx ?? 0];
  if (!img) return fallback;

  fallback = img.fallbackUrl;

  if (!imageRequirement) return fallback;

  const opt = img.optimizations[imageRequirement[reqIdx].optimization];
  if (!opt) return fallback;

  return opt.url;
}

export function hasOptimizedEdpImageUrl(
  imageOptimizations: DailyProgramImageOptimization[] | undefined,
  imageRequirement: ImageRequirement[],
  reqIdx: number,
  imgIdx?: number
): boolean {
  if (!imageOptimizations) return false;

  const img = imageOptimizations[imgIdx ?? 0];
  if (!img) return false;

  const opt = img.optimizations[imageRequirement[reqIdx].optimization];
  if (!opt) return false;

  return true;
}

export function makeDailyProgramImages(
  images: DailyProgramImage[],
  imageOptimizations: DailyProgramImageOptimization[],
  baseUrl: string
) {
  return {
    images: images.map(img => ({
      url: urljoin(baseUrl, img.url),
    })),
    // add base url
    imageOptimizations: imageOptimizations.map(opt => ({
      fallbackUrl: urljoin(baseUrl, opt.fallbackUrl),
      optimizations: Object.fromEntries(
        Object.entries(opt.optimizations).map(([key, value]) => [
          key,
          { url: urljoin(baseUrl, (value as { url: string }).url) },
        ])
      ),
    })),
  };
}
